import { Video } from "../types/Video";
import { parseISO } from "date-fns";
import initialVideosData from "../data/initial_videos.json";

/// 動画統計情報の型定義
export interface VideoStats {
  totalVideos: number;
  totalViews: number;
  totalLikes: number;
  averageViews: number;
  mostPopularVideo: Video | null;
  totalDuration: string;
}

/// WebSocketメッセージの型定義
interface WebSocketMessage {
  type: string;
  clientId: string;
  data?: any;
  timestamp: number;
}

/// プレイヤー同期状態の型定義
interface PlayerSyncState {
  videoId: string;
  currentTime: number;
  duration: number;
  state: "UNSTARTED" | "ENDED" | "PLAYING" | "PAUSED" | "BUFFERING" | "CUED";
  lastUpdateTime: number;
}

/// コメントデータの型定義
export interface CommentData {
  id: string;
  clientId: string; // 下位互換性のため保持
  userId: string; // ユーザー固有ID（ブラウザ固有）
  username: string;
  text: string;
  timestamp: number;
}

/// 新しい型定義: スキップ投票ステータス
export interface SkipVoteStatus {
  totalViewers: number; // 現在の視聴者数
  voters: number; // 投票に参加した人数
  skipVotes: number; // スキップに賛成した票数
  endsAt: number; // 投票終了時刻（UNIX エポック ms）
  clientVote?: "SKIP" | "STAY" | null; // 自分の投票選択（リロード時復元用）
}

/// 動画データを取得するサービス
export class VideoService {
  /// 全ての動画データを取得
  static async getAllVideos(): Promise<Video[]> {
    try {
      // インポートされたデータを直接返す
      return allVideos;
    } catch (error) {
      console.error("動画データ取得エラー:", error);
      return [];
    }
  }

  /// 最新の動画を取得（公開日順）
  static async getLatestVideos(limit: number = 6): Promise<Video[]> {
    const videos = await this.getAllVideos();
    return videos
      .sort(
        (a, b) =>
          new Date(b.publishedAt).getTime() - new Date(a.publishedAt).getTime()
      )
      .slice(0, limit);
  }

  /// 人気の動画を取得（視聴回数順）
  static async getPopularVideos(limit: number = 6): Promise<Video[]> {
    const videos = await this.getAllVideos();
    return videos.sort((a, b) => b.viewCount - a.viewCount).slice(0, limit);
  }

  /// IDで動画を取得
  static async getVideoById(id: string): Promise<Video | null> {
    const videos = await this.getAllVideos();
    return videos.find((video) => video.id === id) || null;
  }

  /// タグで動画を検索
  static async getVideosByTag(tag: string, limit?: number): Promise<Video[]> {
    const videos = await this.getAllVideos();
    const filteredVideos = videos.filter((video) =>
      video.tags.some((videoTag) => videoTag.includes(tag))
    );
    return limit ? filteredVideos.slice(0, limit) : filteredVideos;
  }

  /// 動画統計情報を取得
  static async getVideoStats(): Promise<VideoStats> {
    const videos = await this.getAllVideos();

    if (videos.length === 0) {
      return {
        totalVideos: 0,
        totalViews: 0,
        totalLikes: 0,
        averageViews: 0,
        mostPopularVideo: null,
        totalDuration: "00:00:00",
      };
    }

    const totalViews = videos.reduce((sum, video) => sum + video.viewCount, 0);
    const totalLikes = videos.reduce((sum, video) => sum + video.likeCount, 0);
    const averageViews = Math.round(totalViews / videos.length);
    const mostPopularVideo = videos.reduce((prev, current) =>
      prev.viewCount > current.viewCount ? prev : current
    );

    // 総配信時間を計算
    const totalSeconds = videos.reduce((sum, video) => {
      const [hours, minutes, seconds] = video.duration.split(":").map(Number);
      return sum + hours * 3600 + minutes * 60 + seconds;
    }, 0);

    const totalHours = Math.floor(totalSeconds / 3600);
    const totalMinutes = Math.floor((totalSeconds % 3600) / 60);
    const remainingSeconds = totalSeconds % 60;
    const totalDuration = `${totalHours
      .toString()
      .padStart(2, "0")}:${totalMinutes
      .toString()
      .padStart(2, "0")}:${remainingSeconds.toString().padStart(2, "0")}`;

    return {
      totalVideos: videos.length,
      totalViews,
      totalLikes,
      averageViews,
      mostPopularVideo,
      totalDuration,
    };
  }

  /// 動画データの日付範囲を取得
  static async getVideoDateRange(): Promise<{ minDate: Date; maxDate: Date }> {
    const videos = await this.getAllVideos();
    let minDate = new Date();
    let maxDate = new Date("1970-01-01");

    videos.forEach((video) => {
      try {
        const date = parseISO(video.publishedAt);
        if (date < minDate) {
          minDate = date;
        }
        if (date > maxDate) {
          maxDate = date;
        }
      } catch {
        // 日付パースエラーは無視
      }
    });

    return { minDate, maxDate };
  }

  /// YouTube動画IDをURLから抽出する
  static extractVideoId(url: string): string | null {
    const patterns = [
      /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/,
      /^([a-zA-Z0-9_-]{11})$/, // 直接的な動画ID
    ];

    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match) {
        return match[1];
      }
    }

    return null;
  }

  /// 動画時間を秒に変換する
  static parseDuration(duration: string): number {
    const match = duration.match(/PT(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?/);
    if (!match) return 0;

    const hours = parseInt(match[1] || "0");
    const minutes = parseInt(match[2] || "0");
    const seconds = parseInt(match[3] || "0");

    return hours * 3600 + minutes * 60 + seconds;
  }

  /// 秒を時間文字列に変換する
  static formatDuration(seconds: number): string {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);

    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, "0")}:${secs
        .toString()
        .padStart(2, "0")}`;
    } else {
      return `${minutes}:${secs.toString().padStart(2, "0")}`;
    }
  }
}

/// 動画同期サービス
export class VideoSyncService {
  private ws: WebSocket | null = null;
  private clientId: string;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectInterval = 1000;
  private isConnecting = false;

  /// イベントハンドラー
  private onConnectionChange?: (connected: boolean) => void;
  private onViewerCountUpdate?: (count: number) => void;
  private onPlayerSync?: (state: PlayerSyncState) => void;
  private onCommentReceived?: (comment: CommentData) => void;
  private onVoteUpdate?: (voteData: {
    videoId: string;
    voteCount: number;
  }) => void;
  private onSkipVoteStatusReceived?: (status: SkipVoteStatus) => void;
  private onSkipVoteResultReceived?: (result: { skipped: boolean }) => void;
  private onSessionResetReceived?: () => void;
  private onMasterChangeReceived?: (data: {
    newMasterId: string;
    previousMasterId: string | null;
    needsVideoLoad?: boolean;
  }) => void;
  private onServerVideoReceived?: (videoInfo: {
    videoId: string;
    title: string;
    duration: string;
    thumbnailPath: string;
  }) => void;
  private onCommentHistoryReceived?: (comments: CommentData[]) => void;

  constructor() {
    this.clientId = this.getOrCreateClientId();
    this.setupVisibilityChangeListener();
  }

  /// クライアントID管理
  private getOrCreateClientId(): string {
    let clientId = localStorage.getItem("watch-together-client-id");
    if (!clientId) {
      // crypto.randomUUID()が利用可能かチェックし、そうでなければフォールバックする
      if (
        typeof window !== "undefined" &&
        window.crypto &&
        window.crypto.randomUUID
      ) {
        clientId = window.crypto.randomUUID();
      } else {
        clientId = generateUUID();
      }
      localStorage.setItem("watch-together-client-id", clientId);
    }
    return clientId;
  }

  /// WebSocket接続
  async connect(): Promise<boolean> {
    // 既に接続中または接続済みの場合は重複接続を防ぐ
    if (this.isConnecting || this.ws?.readyState === WebSocket.OPEN) {
      return this.ws?.readyState === WebSocket.OPEN;
    }

    this.isConnecting = true;

    try {
      // 環境変数からWebSocket URLを取得
      // 設定されていない場合は自動判定
      let wsUrl: string;

      if (process.env.REACT_APP_WS_URL) {
        // 環境変数が設定されている場合はそれを使用
        wsUrl = process.env.REACT_APP_WS_URL;
      } else {
        // 環境変数が設定されていない場合は従来の自動判定ロジック
        const isDevelopment = window.location.hostname === "localhost";
        const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";

        if (isDevelopment) {
          wsUrl = "ws://127.0.0.1:8787/api/watch-session";
        } else {
          /// 本番環境では常に marleastraea.com（wwwなし）を使用
          wsUrl = `${protocol}//marleastraea.com/api/watch-session`;
        }
      }

      console.log(`WebSocket接続先: ${wsUrl}`);
      this.ws = new WebSocket(wsUrl);

      // 接続タイムアウトを設定（10秒）
      const connectionTimeout = setTimeout(() => {
        if (this.ws?.readyState !== WebSocket.OPEN) {
          console.warn("WebSocket接続タイムアウト");
          this.ws?.close();
          this.isConnecting = false;
        }
      }, 10000);

      this.ws.onopen = () => {
        clearTimeout(connectionTimeout);
        this.isConnecting = false;
        this.reconnectAttempts = 0;
        this.onConnectionChange?.(true);
        this.startHeartbeat();
      };

      this.ws.onmessage = (event) => {
        this.handleMessage(event.data);
      };

      this.ws.onclose = (event) => {
        clearTimeout(connectionTimeout);
        this.isConnecting = false;
        this.ws = null;
        this.onConnectionChange?.(false);

        // 正常な切断（1000）以外の場合のみ再接続を試行
        if (event.code !== 1000) {
          this.attemptReconnect();
        }
      };

      this.ws.onerror = (error) => {
        console.error("WebSocketエラー:", error);
        clearTimeout(connectionTimeout);
        this.isConnecting = false;
      };

      // 接続完了を待つ
      return new Promise((resolve) => {
        const checkConnection = () => {
          if (this.ws?.readyState === WebSocket.OPEN) {
            resolve(true);
          } else if (
            this.ws?.readyState === WebSocket.CLOSED ||
            this.ws?.readyState === WebSocket.CLOSING
          ) {
            resolve(false);
          } else {
            setTimeout(checkConnection, 100);
          }
        };
        checkConnection();
      });
    } catch (error) {
      console.error("WebSocket接続失敗:", error);
      this.isConnecting = false;
      return false;
    }
  }

  /// WebSocket切断
  disconnect(): void {
    if (this.ws) {
      this.sendMessage("LEAVE", {});
      this.ws.close();
      this.ws = null;
    }
    this.onConnectionChange?.(false);
  }

  /// 再接続試行
  private attemptReconnect(): void {
    if (this.reconnectAttempts < this.maxReconnectAttempts) {
      this.reconnectAttempts++;

      // 再接続中状態を通知
      this.onConnectionChange?.(false);

      setTimeout(() => {
        this.connect();
      }, this.reconnectInterval * this.reconnectAttempts);
    } else {
      console.error("最大再接続試行回数に達しました");
      this.onConnectionChange?.(false);
    }
  }

  /// ハートビート開始
  private startHeartbeat(): void {
    const sendHeartbeat = () => {
      if (
        document.visibilityState === "visible" &&
        this.ws?.readyState === WebSocket.OPEN
      ) {
        this.sendMessage("HEARTBEAT", {});
      }
    };

    // 初回送信
    sendHeartbeat();

    // 30秒間隔でハートビート送信（負荷軽減のため間隔を倍に）
    setInterval(sendHeartbeat, 30000);
  }

  /// メッセージ送信
  private sendMessage(type: string, data: any): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      const message: WebSocketMessage = {
        type,
        clientId: this.clientId,
        data,
        timestamp: Date.now(),
      };
      this.ws.send(JSON.stringify(message));
    } else {
      console.error(
        `WebSocket接続なし - type: ${type}, readyState: ${this.ws?.readyState}`
      );
    }
  }

  /// メッセージ受信処理
  private handleMessage(data: string): void {
    try {
      const message: WebSocketMessage = JSON.parse(data);

      switch (message.type) {
        case "VIEWER_COUNT_UPDATE":
          this.onViewerCountUpdate?.(message.data.count);
          break;
        case "PLAYER_SYNC":
          this.onPlayerSync?.(message.data);
          break;
        case "SERVER_VIDEO":
          // サーバーから動画情報を受信
          this.onServerVideoReceived?.(message.data);
          break;
        case "NEW_COMMENT":
          if (this.onCommentReceived) {
            this.onCommentReceived(message.data);
          } else {
            console.warn("onCommentReceivedコールバックが設定されていません");
          }
          break;
        case "COMMENT_HISTORY":
          if (this.onCommentHistoryReceived) {
            this.onCommentHistoryReceived(message.data.comments || []);
          } else {
            console.warn(
              "onCommentHistoryReceivedコールバックが設定されていません"
            );
          }
          break;
        case "VOTE_UPDATE":
          this.onVoteUpdate?.(message.data);
          break;
        case "SKIP_VOTE_STATUS":
          this.onSkipVoteStatusReceived?.(message.data as SkipVoteStatus);
          break;
        case "SKIP_VOTE_RESULT":
          this.onSkipVoteResultReceived?.(message.data);
          break;
        case "SESSION_RESET_COMPLETE":
          this.onSessionResetReceived?.();
          break;
        case "MASTER_CHANGED":
          this.onMasterChangeReceived?.(message.data);
          break;
        case "SYNC":
          // サーバーホストからの同期データを処理
          if (message.data.videoInfo) {
            this.onServerVideoReceived?.(message.data.videoInfo);
          }
          // 通常の同期処理
          this.onPlayerSync?.(message.data);
          break;
      }
    } catch (error) {
      console.error("メッセージ処理エラー:", error);
    }
  }

  /// プレイヤー状態更新送信（マスタークライアントのみ）
  sendPlayerState(state: PlayerSyncState): void {
    this.sendMessage("PLAYER_STATE", state);
  }

  /// 同期リクエスト送信
  requestSync(): void {
    this.sendMessage("SYNC_REQUEST", {});
  }

  /// セッションリセット要求送信
  requestSessionReset(): void {
    this.sendMessage("SESSION_RESET", {});
  }

  /// コメント履歴要求送信
  requestCommentHistory(): void {
    this.sendMessage("REQUEST_COMMENT_HISTORY", {});
  }

  /// コメント送信
  sendComment(username: string, text: string): void {
    this.sendMessage("COMMENT", { username, text });
  }

  /// userIdを含むコメント送信（新機能）
  sendCommentWithUserId(userId: string, username: string, text: string): void {
    this.sendMessage("COMMENT", { userId, username, text });
  }

  /// 投票送信
  sendVote(videoId: string): void {
    this.sendMessage("VOTE", { videoId });
  }

  /// スキップ投票開始要求（最初の視聴者が押したとき）
  startSkipVote(): void {
    this.sendMessage("SKIP_VOTE_START", {});
  }

  /// スキップ投票の賛成/反対を送信
  sendSkipVote(choice: "SKIP" | "STAY"): void {
    this.sendMessage("SKIP_VOTE_VOTE", { choice });
  }

  /// イベントリスナー設定
  onConnection(callback: (connected: boolean) => void): void {
    this.onConnectionChange = callback;
  }

  onViewerCount(callback: (count: number) => void): void {
    this.onViewerCountUpdate = callback;
  }

  onSync(callback: (state: PlayerSyncState) => void): void {
    this.onPlayerSync = callback;
  }

  onComment(callback: (comment: CommentData) => void): void {
    this.onCommentReceived = callback;
  }

  onVote(
    callback: (voteData: { videoId: string; voteCount: number }) => void
  ): void {
    this.onVoteUpdate = callback;
  }

  onSessionReset(callback: () => void): void {
    this.onSessionResetReceived = callback;
  }

  onMasterChange(
    callback: (data: {
      newMasterId: string;
      previousMasterId: string | null;
      needsVideoLoad?: boolean;
    }) => void
  ): void {
    this.onMasterChangeReceived = callback;
  }

  onServerVideo(
    callback: (videoInfo: {
      videoId: string;
      title: string;
      duration: string;
      thumbnailPath: string;
    }) => void
  ): void {
    this.onServerVideoReceived = callback;
  }

  onSkipVoteStatus(callback: (status: SkipVoteStatus) => void): void {
    this.onSkipVoteStatusReceived = callback;
  }

  onSkipVoteResult(callback: (result: { skipped: boolean }) => void): void {
    this.onSkipVoteResultReceived = callback;
  }

  onCommentHistory(callback: (comments: CommentData[]) => void): void {
    this.onCommentHistoryReceived = callback;
  }

  /// 接続状態確認
  get isConnected(): boolean {
    return this.ws?.readyState === WebSocket.OPEN;
  }

  /// クライアントID取得
  get getClientId(): string {
    return this.clientId;
  }

  private setupVisibilityChangeListener(): void {
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "hidden") {
        // バックグラウンドになったら切断（任意）
        // this.disconnect();
      }
      if (document.visibilityState === "visible") {
        // フォアグラウンドになったら再接続
        if (!this.isConnected && !this.isConnecting) {
          this.attemptReconnect();
        }
      }
    });
  }
}

/// 簡易的なUUID v4ジェネレーター
function generateUUID() {
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function (c) {
    const r = (Math.random() * 16) | 0,
      v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

// 型アサーションを使用して、インポートされたデータが期待する構造であることを確認
const allVideos: Video[] = initialVideosData as Video[];

/**
 * 初期動画リストを取得する
 * @returns {Promise<Video[]>} 初期動画の配列
 */
export const getInitialVideos = async (): Promise<Video[]> => {
  try {
    // インポートしたデータを直接返す
    return allVideos;
  } catch (error) {
    console.error("初期動画データの読み込みに失敗しました:", error);
    return [];
  }
};
